import { apiFetch } from '@app/lib/api'
import type {
  PurchaseDetailResponse,
  PurchaseFilter,
  PurchaseListResponse,
  PurchaseSummaryResponse,
} from '../../../worker/api/satinalma.types'

/**
 * Data layer for the purchasing module: typed fetch helpers + TanStack Query
 * keys. Response types come from the Worker's API contract
 * (`worker/api/satinalma.types.ts`), so the API and the screens share a single
 * contract and cannot drift apart.
 */

export type {
  PurchaseColorBreakdown,
  PurchaseCurrencyBreakdown,
  PurchaseCurrencyTotal,
  PurchaseDetailHeader,
  PurchaseDetailResponse,
  PurchaseDetailSummary,
  PurchaseFilter,
  PurchaseLine,
  PurchaseListResponse,
  PurchaseListRow,
  PurchaseListSummary,
  PurchaseReceiptStatus,
  PurchaseSizeCell,
  PurchaseStatus,
  PurchaseSummaryResponse,
  PurchaseSummaryTotals,
  PurchaseSupplierExposure,
} from '../../../worker/api/satinalma.types'

export interface PurchaseListParams {
  query?: string
  status?: PurchaseFilter
  page?: number
  pageSize?: number
}

function listSearch(params: PurchaseListParams): string {
  const search = new URLSearchParams()
  if (params.query) search.set('query', params.query)
  if (params.status && params.status !== 'all') search.set('status', params.status)
  if (params.page && params.page > 1) search.set('page', String(params.page))
  if (params.pageSize) search.set('pageSize', String(params.pageSize))
  const qs = search.toString()
  return qs ? `?${qs}` : ''
}

export const purchasingApi = {
  list: (params: PurchaseListParams) =>
    apiFetch<PurchaseListResponse>(`/satinalma${listSearch(params)}`),
  summary: () => apiFetch<PurchaseSummaryResponse>('/satinalma/summary'),
  /** `sipNo` is an integer carried as text; the API rejects anything else. */
  detail: (sipNo: string) =>
    apiFetch<PurchaseDetailResponse>(`/satinalma/${encodeURIComponent(sipNo)}`),
}

export const purchasingQueryKeys = {
  all: ['satinalma'] as const,
  list: (params: PurchaseListParams) => ['satinalma', 'list', params] as const,
  summary: () => ['satinalma', 'summary'] as const,
  detail: (sipNo: string) => ['satinalma', 'detail', sipNo] as const,
}
